import axios from "axios";

const API_URL = process.env.REACT_APP_CORE_API_URL;
const OTB_API_URL = process.env.REACT_APP_OTB_API_URL;
const WAREHOUSE_API_URL = process.env.REACT_APP_WAREHOUSE_API_URL;
const TOKEN_API_URL = process.env.REACT_APP_TOKEN_API_URL
const V2_API_URL = process.env.REACT_APP_V2_API_URL;

let cachedToken = null;
let tokenPromise = null;

export const getStorageKey = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return `${parts[0]}token`;
  }
  return "authData";
};


// Handle session timeout
const handleSessionTimeout = async () => {
  window.parent.postMessage("sessionout", "*");
  await new Promise((resolve) => setTimeout(resolve, 500));
};

// -----------------------------------------------------
// Axios Helpers

const authHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});
const getAuthIDFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("authID");
};

let refreshInterval = null;
let isRefreshing = false;

const refreshToken = async () => {
  // Prevent multiple refresh requests
  if (isRefreshing) {
    return cachedToken;
  }

  if (!cachedToken?.refAuthID || !cachedToken?.token) {
    console.error("Refresh token or current token is missing.");
    handleSessionTimeout();
    return null;
  }

  isRefreshing = true;

  try {
    const response = await axios.post(
      `${TOKEN_API_URL}/v2/OpenApiauth/RefreshToken`,
      { refAuthID: cachedToken.refAuthID },
      authHeaders(cachedToken.token)
    );

    if (response.data?.token) {
      return handleV2Token(response.data);
    }

    return null;
  } catch (error) {
    console.error("Auto token refresh failed:", error);
    handleSessionTimeout();
    return null;
  } finally {
    isRefreshing = false;
  }
};

const handleV2Token = (tokenData) => {
  const { refAuth, minutesToExpire } = tokenData;

  // Normalize the v2 token response
  const normalizedTokenData = {
    ...tokenData,
    refAuthID: tokenData.refAuth,
    minutesToExpire: tokenData.minutesToExpire,
    expireTokenInMinutes: tokenData.expireTokenInMinutes,
  };

  // Cache latest token
  cachedToken = normalizedTokenData;

  // Clear any existing refresh timer
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }

  // Schedule the next refresh
  if (refAuth && minutesToExpire) {
    const expiresAtUtc = Date.now() + minutesToExpire * 60 * 1000;

    refreshInterval = setInterval(async () => {
      const minutesRemaining =
        (expiresAtUtc - Date.now()) / (60 * 1000);


      if (minutesRemaining <= 2) {
        clearInterval(refreshInterval);
        refreshInterval = null;

        await refreshToken();
      }
    }, 60 * 1000);
  }

  return cachedToken;
};


let v2ClientsPromise = null;

export const FindClientDetails = async () => {
  if (v2ClientsPromise) {
    return v2ClientsPromise;
  }

  v2ClientsPromise = (async () => {
    try {
      const response = await axios.get(`${V2_API_URL}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching client details:", error);
      v2ClientsPromise = null; // Reset on error to allow retries
      return [];
    }
  })();

  return v2ClientsPromise;
};



export const getToken = async () => {
  if (cachedToken && !isRefreshing) {
    return cachedToken;
  }

  // If a token request is already in flight, wait for it to complete.
  if (tokenPromise) {
    return tokenPromise;
  }

  // Start a new token request
  tokenPromise = (async () => {
    try {
      const authID = getAuthIDFromUrl();
      const params = new URLSearchParams(window.location.search);
      const urlInfo = params.get("urlInfo");
  
      const v2Clients = await FindClientDetails();
      const isV2 = v2Clients.includes(urlInfo);
      const apiVersion = isV2 ? "v2" : "v1";
  
      const response = await axios.post(
        `${TOKEN_API_URL}/${apiVersion}/OpenApiauth/ExchangeOAuth`,
        { token: authID }
      );
  
      if (response.status === 200 && response.data?.token) {
        if (isV2) {
          return handleV2Token(response.data);
        }
        cachedToken = response.data;
        return response.data;
      }
  
      handleSessionTimeout();
      return null;
    } catch (error) {
      console.error("Error exchanging authID for token:", error);
      if (error.response?.status !== 200) {
        handleSessionTimeout();
      }
      return null;
    } finally {
      tokenPromise = null;
    }
  })();
  return tokenPromise;
};



export const validateAuth = async () => {
  const tokenData = await getToken();
  return tokenData?.statusCode === 200 ? tokenData : null;
};

// -----------------------------------------------------
// Fetch Management Groups Dropdown
// -----------------------------------------------------
export const getManagementGroupsList = async () => {
  try {
    const tokenData = await getToken();
    const response = await axios.get(
      `${API_URL}/v1/GetManagementGroupsList`,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching management groups list:", error);
    return [];
  }
};


export const getCorpPcList = async (MgntGrpId) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.get(
      `${OTB_API_URL}/v1/DailySale/GetCorpAndPCListByMgntGrpWithPmsBrandSerTypes?MgntGrpId=${MgntGrpId}`,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching Corp Pc list:", error);
    return [];
  }
};


export const getOTBReport = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${WAREHOUSE_API_URL}/v1/Instant/syncWareHouseGetOTBForecastReportDetails`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching warehouse list:", error);
    return [];
  }
};


export const SaveOTBMemorize = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${OTB_API_URL}/v1/DailySale/OTBMemorizeSaveDetails`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching warehouse list:", error);
    return [];
  }
};



export const ResetOTBMemorize = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${OTB_API_URL}/v1/DailySale/OTBResetDetails`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching warehouse list:", error);
    return [];
  }
};


export const getOTBMemorize = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${OTB_API_URL}/v1/DailySale/OTBMemorizeGetDetails`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching warehouse list:", error);
    return [];
  }
};


export const GetLegalNameorDbName = async () => {
  try {
    const tokenData = await getToken();
    const response = await axios.get(
      `${API_URL}/v1/GetLegalNameorDbName`,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      return response.data;
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching management groups list:", error);
    return [];
  }
};


export const getFiscalYear = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${API_URL}/v1/CorporationDetails`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      if (Array.isArray(response.data)) return response.data;
      if (response.data && typeof response.data === 'object') return [response.data];
      return [];
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching warehouse list:", error);
    return [];
  }
};


export const calculateNextFiscalQuarter = (fiscalData, isMultiple) => {
  if (isMultiple || !fiscalData) return null;

  const { PeriodStartDate, IsFisicalOrFinancial } = fiscalData;
  const isFiscal = IsFisicalOrFinancial == 0;
  const today = new Date();
  let startMonth = 0; // Jan
  let startDay = 1;

  if (isFiscal && PeriodStartDate) {
    const [m, d] = PeriodStartDate.split('/').map(Number);
    if (!isNaN(m) && !isNaN(d)) {
      startMonth = m - 1;
      startDay = d;
    }
  }

  let fiscalYearStart = new Date(today.getFullYear(), startMonth, startDay);
  if (today < fiscalYearStart) {
    fiscalYearStart.setFullYear(today.getFullYear() - 1);
  }

  const getQuarter = (baseStart, qIndex) => {
    const start = new Date(baseStart);
    start.setMonth(baseStart.getMonth() + (qIndex * 3));
    const end = new Date(start);
    end.setMonth(start.getMonth() + 3);
    end.setDate(0);
    return { start, end };
  };

  let currentQIndex = 0;
  for (let i = 0; i < 4; i++) {
    const { start, end } = getQuarter(fiscalYearStart, i);
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    if (today >= start && today <= endOfDay) {
      currentQIndex = i;
      break;
    }
  }

  const nextQ = getQuarter(fiscalYearStart, currentQIndex + 1);

  const formatDate = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  return {
    startDate: formatDate(nextQ.start),
    endDate: formatDate(nextQ.end)
  };
};

export const getPrivileges = async (payload) => {
  try {
    const tokenData = await getToken();
    if (!tokenData?.token) return [];
    const response = await axios.post(
      `${API_URL}/v1/Previlages`,
      payload,
      authHeaders(tokenData?.token)
    );
    if (response.status === 200) {
      return response.data;
    } else {
      handleSessionTimeout();
      return [];
    }
  } catch (error) {
    if (error.response?.status === 401) {
      cachedToken = null;
      handleSessionTimeout();
    }
    console.error("Error fetching privileges:", error);
    return [];
  }
}